import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { ArrowLeft, Save, Lock, Unlock, Download, Users, Mail, Crown, MessageSquare, Inbox, Check, Trash2, MessageSquarePlus, Wrench } from 'lucide-react';
import appConfig from '@/config/appConfig';
import toolsConfig from '@/config/toolsConfig';
import { toast } from 'sonner';

const AdminPanel = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [apps, setApps] = useState([]);
  const [appSettings, setAppSettings] = useState({});
  const [tools, setTools] = useState([]);
  const [toolSettings, setToolSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const [toolLoading, setToolLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [userStats, setUserStats] = useState({ total: 0, premium_count: 0, free_count: 0 });
  const [activeTab, setActiveTab] = useState('apps');
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [messageFilter, setMessageFilter] = useState('all');

  useEffect(() => {
    if (!user) return;
    if (!user.is_admin) {
      toast.error('Admin access only');
      navigate('/dashboard');
      return;
    }

    loadAppSettings();
    loadToolSettings();
    loadUsers();
    loadMessages();
    setApps(appConfig);
    setTools(toolsConfig);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  const loadMessages = async () => {
    const { data, error } = await supabase
      .from('messages')
      .select('*')
      .order('created_at', { ascending: false });

    if (!error && data) {
      setMessages(data);
      setUnreadCount(data.filter((m) => !m.read).length);
    }
  };

  const markAsRead = async (messageId) => {
    const { error } = await supabase.from('messages').update({ read: true }).eq('id', messageId);
    if (error) {
      toast.error('Failed to mark as read');
      return;
    }
    setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, read: true } : m)));
    setUnreadCount((prev) => Math.max(0, prev - 1));
    toast.success('Marked as read');
  };

  const deleteMessage = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;

    const { error } = await supabase.from('messages').delete().eq('id', messageId);
    if (error) {
      toast.error('Failed to delete message');
      return;
    }
    const deletedMessage = messages.find((m) => m.id === messageId);
    setMessages((prev) => prev.filter((m) => m.id !== messageId));
    if (deletedMessage && !deletedMessage.read) {
      setUnreadCount((prev) => Math.max(0, prev - 1));
    }
    toast.success('Message deleted');
  };

  const getFilteredMessages = () => {
    if (messageFilter === 'all') return messages;
    if (messageFilter === 'unread') return messages.filter((m) => !m.read);
    if (messageFilter === 'contact') return messages.filter((m) => m.type === 'contact');
    if (messageFilter === 'app_request') return messages.filter((m) => m.type === 'app_request');
    return messages;
  };

  const loadAppSettings = async () => {
    const { data, error } = await supabase.from('app_settings').select('app_id, is_premium');

    const settings = {};
    appConfig.forEach((app) => {
      settings[app.id] = app.isPremium;
    });

    if (!error && data) {
      data.forEach((row) => {
        settings[row.app_id] = row.is_premium;
      });
    }

    setAppSettings(settings);
  };

  const loadUsers = async () => {
    const { data, error } = await supabase
      .from('admin_user_overview')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      toast.error('Failed to load users');
      return;
    }

    setUsers(data || []);
    const total = data?.length || 0;
    const premium_count = data?.filter((u) => u.is_premium).length || 0;
    setUserStats({ total, premium_count, free_count: total - premium_count });
  };

  const togglePremium = (appId) => {
    setAppSettings((prev) => ({
      ...prev,
      [appId]: !prev[appId],
    }));
  };

  const saveSettings = async () => {
    setLoading(true);
    const rows = Object.entries(appSettings).map(([app_id, is_premium]) => ({
      app_id,
      is_premium,
      updated_at: new Date().toISOString(),
    }));

    const { error } = await supabase.from('app_settings').upsert(rows, { onConflict: 'app_id' });
    setLoading(false);

    if (error) {
      toast.error('Failed to save settings');
      return;
    }
    toast.success('Settings saved! Changes are live immediately.');
  };

  // ── AI tools (tools.doctorgemma.com) — same premium/free model as the apps
  // above, but backed by the tool_settings table and keyed by tool slug. The
  // public tools gallery reads tool_settings to decide which cards are locked.
  const loadToolSettings = async () => {
    const { data, error } = await supabase.from('tool_settings').select('tool_id, is_premium');

    const settings = {};
    toolsConfig.forEach((tool) => {
      settings[tool.id] = tool.isPremium;
    });

    if (!error && data) {
      data.forEach((row) => {
        settings[row.tool_id] = row.is_premium;
      });
    }

    setToolSettings(settings);
  };

  const toggleToolPremium = (toolId) => {
    setToolSettings((prev) => ({
      ...prev,
      [toolId]: !prev[toolId],
    }));
  };

  const saveToolSettings = async () => {
    setToolLoading(true);
    const rows = Object.entries(toolSettings).map(([tool_id, is_premium]) => ({
      tool_id,
      is_premium,
      updated_at: new Date().toISOString(),
    }));

    const { error } = await supabase.from('tool_settings').upsert(rows, { onConflict: 'tool_id' });
    setToolLoading(false);

    if (error) {
      toast.error('Failed to save AI tool settings');
      return;
    }
    toast.success('AI tool settings saved! Changes are live immediately.');
  };

  const exportEmails = (premiumOnly = false) => {
    const rows = premiumOnly ? users.filter((u) => u.is_premium) : users;

    const csvContent = [
      ['Email', 'Name', 'Status', 'Created Date'].join(','),
      ...rows.map((u) => [
        u.email,
        u.display_name,
        u.is_premium ? 'Premium' : 'Free',
        new Date(u.created_at).toLocaleDateString(),
      ].join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `inclusion-apps-${premiumOnly ? 'premium' : 'all'}-users-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);

    toast.success(`Exported ${rows.length} email addresses`);
  };

  if (!user || !user.is_admin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FDFBF7] to-[#E0F7FA] relative overflow-hidden">
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-12">
        <header className="flex justify-between items-center mb-12">
          <button
            data-testid="back-to-dashboard"
            onClick={() => navigate('/dashboard')}
            className="bg-white text-[#2D3748] px-6 py-3 rounded-full font-semibold hover:bg-slate-100 transition-all flex items-center gap-2 shadow-sm"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to dashboard
          </button>

          <h1 className="text-4xl font-bold text-[#2D3748]">Admin panel</h1>

          <div className="w-40"></div>
        </header>

        <div className="flex gap-4 mb-8">
          <button
            onClick={() => setActiveTab('apps')}
            className={`px-6 py-3 rounded-full font-semibold transition-all ${
              activeTab === 'apps'
                ? 'bg-[#81B29A] text-white shadow-lg'
                : 'bg-white text-[#4A5568] hover:bg-slate-100'
            }`}
          >
            App settings
          </button>
          <button
            onClick={() => setActiveTab('tools')}
            className={`px-6 py-3 rounded-full font-semibold transition-all ${
              activeTab === 'tools'
                ? 'bg-[#81B29A] text-white shadow-lg'
                : 'bg-white text-[#4A5568] hover:bg-slate-100'
            }`}
          >
            <Wrench className="w-5 h-5 inline mr-2" />
            AI tools
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`px-6 py-3 rounded-full font-semibold transition-all ${
              activeTab === 'users'
                ? 'bg-[#81B29A] text-white shadow-lg'
                : 'bg-white text-[#4A5568] hover:bg-slate-100'
            }`}
          >
            <Users className="w-5 h-5 inline mr-2" />
            Users ({userStats.total})
          </button>
          <button
            data-testid="messages-tab"
            onClick={() => setActiveTab('messages')}
            className={`px-6 py-3 rounded-full font-semibold transition-all relative ${
              activeTab === 'messages'
                ? 'bg-[#81B29A] text-white shadow-lg'
                : 'bg-white text-[#4A5568] hover:bg-slate-100'
            }`}
          >
            <Inbox className="w-5 h-5 inline mr-2" />
            Messages
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                {unreadCount}
              </span>
            )}
          </button>
        </div>

        {activeTab === 'apps' && (
          <>
            <div className="bg-white rounded-3xl p-8 shadow-xl border border-slate-100 mb-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-[#2D3748] mb-2">Premium app management</h2>
                  <p className="text-[#4A5568]">Toggle which apps require premium membership</p>
                </div>
                <div className="flex gap-4">
                  <div className="text-right">
                    <p className="text-sm text-[#4A5568]">Premium apps:
                      <span className="font-bold text-yellow-600 ml-2">
                        {Object.values(appSettings).filter(Boolean).length}
                      </span>
                    </p>
                    <p className="text-sm text-[#4A5568]">Free apps:
                      <span className="font-bold text-[#81B29A] ml-2">
                        {Object.values(appSettings).filter((v) => !v).length}
                      </span>
                    </p>
                  </div>
                  <button
                    data-testid="save-settings"
                    onClick={saveSettings}
                    disabled={loading}
                    className="bg-gradient-to-r from-[#81B29A] to-[#6A9B84] text-white px-8 py-3 rounded-full font-bold hover:-translate-y-1 transition-all shadow-lg flex items-center gap-2 disabled:opacity-50"
                  >
                    <Save className="w-5 h-5" />
                    {loading ? 'Saving...' : 'Save changes'}
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                {apps.map((app) => (
                  <div
                    key={app.id}
                    className="flex items-center justify-between p-4 rounded-2xl bg-slate-50 hover:bg-slate-100 transition-all"
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                        style={{ backgroundColor: app.color }}
                      >
                        {appSettings[app.id] ? '🔒' : '✓'}
                      </div>
                      <div>
                        <h3 className="font-bold text-[#2D3748]">{app.name}</h3>
                        <p className="text-sm text-[#4A5568]">{app.category}</p>
                      </div>
                    </div>

                    <button
                      data-testid={`toggle-${app.id}`}
                      onClick={() => togglePremium(app.id)}
                      className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all ${
                        appSettings[app.id]
                          ? 'bg-gradient-to-r from-yellow-300 to-yellow-500 text-yellow-900'
                          : 'bg-[#81B29A] text-white'
                      }`}
                    >
                      {appSettings[app.id] ? (
                        <>
                          <Lock className="w-5 h-5" />
                          Premium
                        </>
                      ) : (
                        <>
                          <Unlock className="w-5 h-5" />
                          Free
                        </>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-green-50 rounded-2xl p-6 border-2 border-green-200">
              <h3 className="font-bold text-[#2D3748] mb-3">✅ Database-backed settings</h3>
              <p className="text-[#4A5568] mb-2">
                Changes save to Supabase and apply <strong>immediately</strong> — no file uploads needed.
              </p>
              <p className="text-sm text-[#4A5568]">
                Toggle apps above, click "Save changes", and the platform updates instantly for all users.
              </p>
            </div>
          </>
        )}

        {activeTab === 'tools' && (
          <>
            <div className="bg-white rounded-3xl p-8 shadow-xl border border-slate-100 mb-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-[#2D3748] mb-2">AI tools management</h2>
                  <p className="text-[#4A5568]">Toggle which AI tools (tools.doctorgemma.com) require premium membership</p>
                </div>
                <div className="flex gap-4">
                  <div className="text-right">
                    <p className="text-sm text-[#4A5568]">Premium tools:
                      <span className="font-bold text-yellow-600 ml-2">
                        {Object.values(toolSettings).filter(Boolean).length}
                      </span>
                    </p>
                    <p className="text-sm text-[#4A5568]">Free tools:
                      <span className="font-bold text-[#81B29A] ml-2">
                        {Object.values(toolSettings).filter((v) => !v).length}
                      </span>
                    </p>
                  </div>
                  <button
                    data-testid="save-tool-settings"
                    onClick={saveToolSettings}
                    disabled={toolLoading}
                    className="bg-gradient-to-r from-[#81B29A] to-[#6A9B84] text-white px-8 py-3 rounded-full font-bold hover:-translate-y-1 transition-all shadow-lg flex items-center gap-2 disabled:opacity-50"
                  >
                    <Save className="w-5 h-5" />
                    {toolLoading ? 'Saving...' : 'Save changes'}
                  </button>
                </div>
              </div>

              {tools.length === 0 ? (
                <div className="text-center py-12">
                  <Wrench className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-xl text-[#4A5568]">No AI tools yet</p>
                  <p className="text-sm text-[#718096]">Tools you publish to the gallery will appear here</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {tools.map((tool) => (
                    <div
                      key={tool.id}
                      className="flex items-center justify-between p-4 rounded-2xl bg-slate-50 hover:bg-slate-100 transition-all"
                    >
                      <div className="flex items-center gap-4">
                        <div
                          className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                          style={{ backgroundColor: tool.color }}
                        >
                          {toolSettings[tool.id] ? '🔒' : '✓'}
                        </div>
                        <div>
                          <h3 className="font-bold text-[#2D3748]">{tool.name}</h3>
                          <p className="text-sm text-[#4A5568]">{tool.category}</p>
                        </div>
                      </div>

                      <button
                        data-testid={`toggle-tool-${tool.id}`}
                        onClick={() => toggleToolPremium(tool.id)}
                        className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all ${
                          toolSettings[tool.id]
                            ? 'bg-gradient-to-r from-yellow-300 to-yellow-500 text-yellow-900'
                            : 'bg-[#81B29A] text-white'
                        }`}
                      >
                        {toolSettings[tool.id] ? (
                          <>
                            <Lock className="w-5 h-5" />
                            Premium
                          </>
                        ) : (
                          <>
                            <Unlock className="w-5 h-5" />
                            Free
                          </>
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-green-50 rounded-2xl p-6 border-2 border-green-200">
              <h3 className="font-bold text-[#2D3748] mb-3">✅ Database-backed settings</h3>
              <p className="text-[#4A5568] mb-2">
                Changes save to Supabase and apply <strong>immediately</strong> on the AI tools gallery — no redeploy needed.
              </p>
              <p className="text-sm text-[#4A5568]">
                Toggle tools above, click "Save changes", and tools.doctorgemma.com locks or unlocks each card for all visitors.
              </p>
            </div>
          </>
        )}

        {activeTab === 'users' && (
          <>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-3xl p-6 shadow-lg border border-slate-100">
                <Users className="w-12 h-12 text-[#81B29A] mb-4" />
                <p className="text-[#4A5568] text-sm mb-1">Total users</p>
                <p className="text-4xl font-bold text-[#2D3748]">{userStats.total}</p>
              </div>
              <div className="bg-white rounded-3xl p-6 shadow-lg border border-slate-100">
                <Crown className="w-12 h-12 text-yellow-500 mb-4" />
                <p className="text-[#4A5568] text-sm mb-1">Premium members</p>
                <p className="text-4xl font-bold text-yellow-600">{userStats.premium_count}</p>
              </div>
              <div className="bg-white rounded-3xl p-6 shadow-lg border border-slate-100">
                <Unlock className="w-12 h-12 text-[#81B29A] mb-4" />
                <p className="text-[#4A5568] text-sm mb-1">Free members</p>
                <p className="text-4xl font-bold text-[#81B29A]">{userStats.free_count}</p>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-xl border border-slate-100 mb-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-[#2D3748]">Export email lists</h2>
                <div className="flex gap-3">
                  <button
                    data-testid="export-all-emails"
                    onClick={() => exportEmails(false)}
                    className="bg-[#81B29A] text-white px-6 py-3 rounded-full font-semibold hover:-translate-y-1 transition-all shadow-lg flex items-center gap-2"
                  >
                    <Download className="w-5 h-5" />
                    Export all users
                  </button>
                  <button
                    data-testid="export-premium-emails"
                    onClick={() => exportEmails(true)}
                    className="bg-gradient-to-r from-yellow-300 to-yellow-500 text-yellow-900 px-6 py-3 rounded-full font-semibold hover:-translate-y-1 transition-all shadow-lg flex items-center gap-2"
                  >
                    <Mail className="w-5 h-5" />
                    Export premium only
                  </button>
                </div>
              </div>

              <p className="text-[#4A5568] mb-6">
                Download CSV files with email addresses, names, membership status, and signup dates.
              </p>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-slate-200">
                      <th className="text-left py-3 px-4 text-[#2D3748] font-semibold">Name</th>
                      <th className="text-left py-3 px-4 text-[#2D3748] font-semibold">Email</th>
                      <th className="text-left py-3 px-4 text-[#2D3748] font-semibold">Status</th>
                      <th className="text-left py-3 px-4 text-[#2D3748] font-semibold">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.slice(0, 10).map((u) => (
                      <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4 text-[#2D3748]">{u.display_name}</td>
                        <td className="py-3 px-4 text-[#4A5568]">{u.email}</td>
                        <td className="py-3 px-4">
                          {u.is_premium ? (
                            <span className="premium-badge">
                              <Crown className="w-3 h-3" />
                              Premium
                            </span>
                          ) : (
                            <span className="px-3 py-1 bg-slate-100 text-slate-600 text-xs rounded-full font-medium">
                              Free
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-[#4A5568] text-sm">
                          {new Date(u.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {users.length > 10 && (
                  <p className="text-center text-[#4A5568] text-sm mt-4">
                    Showing 10 of {users.length} users. Export to see all.
                  </p>
                )}
              </div>
            </div>
          </>
        )}

        {activeTab === 'messages' && (
          <>
            <div className="grid md:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-3xl p-6 shadow-lg border border-slate-100">
                <Inbox className="w-12 h-12 text-[#81B29A] mb-4" />
                <p className="text-[#4A5568] text-sm mb-1">Total messages</p>
                <p className="text-4xl font-bold text-[#2D3748]">{messages.length}</p>
              </div>
              <div className="bg-white rounded-3xl p-6 shadow-lg border border-slate-100">
                <MessageSquare className="w-12 h-12 text-red-500 mb-4" />
                <p className="text-[#4A5568] text-sm mb-1">Unread</p>
                <p className="text-4xl font-bold text-red-500">{unreadCount}</p>
              </div>
              <div className="bg-white rounded-3xl p-6 shadow-lg border border-slate-100">
                <Mail className="w-12 h-12 text-blue-500 mb-4" />
                <p className="text-[#4A5568] text-sm mb-1">Contact messages</p>
                <p className="text-4xl font-bold text-blue-600">{messages.filter((m) => m.type === 'contact').length}</p>
              </div>
              <div className="bg-white rounded-3xl p-6 shadow-lg border border-slate-100">
                <MessageSquarePlus className="w-12 h-12 text-purple-500 mb-4" />
                <p className="text-[#4A5568] text-sm mb-1">App requests</p>
                <p className="text-4xl font-bold text-purple-600">{messages.filter((m) => m.type === 'app_request').length}</p>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-xl border border-slate-100 mb-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-[#2D3748]">Message inbox</h2>
                <div className="flex gap-2">
                  {['all', 'unread', 'contact', 'app_request'].map((filter) => (
                    <button
                      key={filter}
                      data-testid={`message-filter-${filter}`}
                      onClick={() => setMessageFilter(filter)}
                      className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                        messageFilter === filter
                          ? 'bg-[#81B29A] text-white shadow-md'
                          : 'bg-slate-100 text-[#4A5568] hover:bg-slate-200'
                      }`}
                    >
                      {filter === 'all' && 'All'}
                      {filter === 'unread' && 'Unread'}
                      {filter === 'contact' && 'Contact'}
                      {filter === 'app_request' && 'App requests'}
                    </button>
                  ))}
                </div>
              </div>

              {getFilteredMessages().length === 0 ? (
                <div className="text-center py-12">
                  <Inbox className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-xl text-[#4A5568]">No messages found</p>
                  <p className="text-sm text-[#718096]">Messages from users will appear here</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {getFilteredMessages().map((message) => (
                    <div
                      key={message.id}
                      data-testid={`message-${message.id}`}
                      className={`p-6 rounded-2xl border-2 transition-all ${
                        message.read
                          ? 'bg-slate-50 border-slate-200'
                          : 'bg-white border-l-4 border-l-[#81B29A] border-slate-200 shadow-md'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            message.type === 'app_request'
                              ? 'bg-purple-100'
                              : 'bg-blue-100'
                          }`}>
                            {message.type === 'app_request' ? (
                              <MessageSquarePlus className="w-5 h-5 text-purple-600" />
                            ) : (
                              <Mail className="w-5 h-5 text-blue-600" />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-[#2D3748]">
                                {message.username || message.email}
                              </span>
                              <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                                message.type === 'app_request'
                                  ? 'bg-purple-100 text-purple-700'
                                  : 'bg-blue-100 text-blue-700'
                              }`}>
                                {message.type === 'app_request' ? 'App request' : 'Contact'}
                              </span>
                              {!message.read && (
                                <span className="w-2 h-2 bg-[#81B29A] rounded-full"></span>
                              )}
                            </div>
                            <p className="text-sm text-[#718096]">{message.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-[#718096]">
                            {new Date(message.created_at).toLocaleString()}
                          </span>
                          {!message.read && (
                            <button
                              data-testid={`mark-read-${message.id}`}
                              onClick={() => markAsRead(message.id)}
                              className="p-2 text-[#81B29A] hover:bg-[#81B29A]/10 rounded-full transition-colors"
                              title="Mark as read"
                            >
                              <Check className="w-5 h-5" />
                            </button>
                          )}
                          <button
                            data-testid={`delete-message-${message.id}`}
                            onClick={() => deleteMessage(message.id)}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors"
                            title="Delete message"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                      <div className="bg-slate-100 rounded-xl p-4">
                        <p className="text-[#4A5568] whitespace-pre-wrap">{message.message}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
